import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusCode } from '../components/Alert.tsx';
import CategoriesController from '../controllers/CategoriesController.ts';
import JournalNameController from '../controllers/JournalNameController.ts';
import MeasurementUnitsController from '../controllers/MeasurementUnitsController.ts';
import StoresController from '../controllers/StoresController.ts';
import UsersController from '../controllers/UsersController.ts';
import { Category } from '../models/category.ts';
import { Role } from '../models/enums/role.ts';
import { JournalName } from '../models/JournalName.ts';
import { MeasurementUnit } from '../models/measurementUnits/measurementUnit.ts';
import { BaseSearchParam } from '../models/searchParams/baseSearchParam.ts';
import { TableSearchParam } from '../models/searchParams/tableSearchParam.ts';
import { Store } from '../models/stores/store.ts';
import { BodyMeasurements } from '../models/users/bodyMeasurements.ts';
import { ChangePasswordModel } from '../models/users/changePasswordModel.ts';
import { UserGoals } from '../models/users/userGoals.ts';
import { UserLoginModel } from '../models/users/userLoginModel.ts';
import { UserRegisterModel } from '../models/users/userRegisterModel.ts';
import { UserRepresentation } from '../models/users/userRepresentation.ts';
import { useAlert } from './alertProvider.tsx';

type UsersContextType = {
    isLoggedIn: boolean;
    userID: number | null;
    register: (userRegisterModel: UserRegisterModel) => void;
    login: (userLoginModel: UserLoginModel) => void;
    changePassword: (changePasswordModel: ChangePasswordModel) => void;
    logout: () => void;
    isInRole: (role: Role) => boolean;

    profile: UserRepresentation;
    fetchProfile: () => void;
    updateProfile: (entity: UserRepresentation) => void;
    insertBodyMeasurements: (entity: BodyMeasurements) => void;
    insertUserGoals: (entity: UserGoals) => void;

    categories: Category[];
    measurementUnits: MeasurementUnit[];
    stores: Store[];
    journalNames: JournalName[];
    setJournalNames: (journalNames: JournalName[]) => void;
};

const UsersContext = createContext<UsersContextType>({} as UsersContextType);

export const UsersContextProvider = ({ children }: { children: any }) => {
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const [userID, setUserID] = useState<number | null>(parseInt(localStorage.getItem("userID")!) || null);
    const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(userID != null);
    const [profile, setProfile] = useState<UserRepresentation>({} as UserRepresentation);

    const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 10000 });
    const [searchParam, setSearchParam] = useState<BaseSearchParam>({} as BaseSearchParam);
    const [categories, setCategories] = useState<Category[]>([]);
    const [measurementUnits, setMeasurementUnits] = useState<MeasurementUnit[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [journalNames, setJournalNames] = useState<JournalName[]>([]);

    useEffect(() => {
        setIsLoggedIn(userID != null)

        fetchCategories();
        fetchMeasurementUnits();
        fetchStores();
        if (userID != null) {
            fetchProfile();
            fetchJournalNames();
        }
    }, [userID])

    const register = async (userRegisterModel: UserRegisterModel) => {
        try {
            await UsersController.register(userRegisterModel)
                .then((result) => {
                    if (result.notificationStatus == StatusCode.success) {
                        localStorage.setItem('accessToken', result.accessToken);
                        localStorage.setItem('refreshToken', result.refreshToken);
                        setUserID(result.userID);
                        localStorage.setItem('userID', result.userID);
                        setUserRole(result.userRole!);
                        localStorage.setItem('userRole', result.userRole);
                        navigate(0);
                    }
                    else
                        showAlert(result.notificationStatus, result.notificationMessage);
                });
        } catch (error) {
            console.error('Error registering user:', error);
        }
    }

    const login = async (userLoginModel: UserLoginModel) => {
        try {
            await UsersController.login(userLoginModel)
                .then((result) => {
                    if (result.notificationStatus == StatusCode.success) {
                        localStorage.setItem('accessToken', result.accessToken);
                        localStorage.setItem('refreshToken', result.refreshToken);
                        setUserID(result.userID);
                        localStorage.setItem('userID', result.userID);
                        setUserRole(result.userRole!);
                        localStorage.setItem('userRole', result.userRole);
                        navigate(0);
                    }
                    else
                        showAlert(result.notificationStatus, result.notificationMessage);
                });
        } catch (error) {
            console.error('Error logging in:', error);
        }
    }

    const changePassword = async (changePasswordModel: ChangePasswordModel) => {
        try {
            await UsersController.changePassword(changePasswordModel)
                .then((result) => {
                    if (result.notificationStatus == StatusCode.success) {
                        setUserID(result.userID);
                        localStorage.setItem('userID', result.userID);
                        setUserRole(result.userRole!);
                        localStorage.setItem('userRole', result.userRole);
                        navigate(0);
                        showAlert(result.notificationStatus, "Password changed successfully");
                    }
                    else
                        showAlert(result.notificationStatus, result.notificationMessage);
                });
        } catch (error) {
            console.error('Error logging in:', error);
        }
    }

    const logout = async () => {
        try {
            if ('caches' in window) {
                caches.keys().then(function (names) {
                    for (let name of names) {
                        caches.delete(name);
                    }
                });
            }
            await UsersController.logout()
                .then((result) => {
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    setUserID(null);
                    localStorage.removeItem('userID');
                    setUserRole('');
                    localStorage.removeItem('userRole');
                    if (result.notificationStatus == StatusCode.success) {
                        navigate(0);
                    }
                    else
                        showAlert(result.notificationStatus, result.notificationMessage);
                });
        } catch (error) {
            console.error('Error logging out:', error);
        }
    }

    const fetchProfile = async () => {
        try {
            await UsersController.getProfile()
                .then((result) => {
                    if (result.notificationStatus == StatusCode.success) {
                        setProfile(result);
                    }
                    else
                        showAlert(result.notificationStatus, result.notificationMessage);
                });
        } catch (error) {
            console.error('Error fetching user profile:', error);
        }
    }
    const updateProfile = async (entity: UserRepresentation) => {
        try {
            await UsersController.updateProfile(entity)
                .then((result) => {
                    showAlert(result.notificationStatus, result.notificationMessage);
                    if (result.notificationStatus == StatusCode.success) {
                        fetchProfile();
                        navigate(-1);
                    }
                });
        } catch (error) {
            console.error('Error updating user profile:', error);
        }
    }

    const insertBodyMeasurements = async (entity: BodyMeasurements) => {
        try {
            await UsersController.postBodyMeasurements(entity)
                .then((result) => {
                    showAlert(result.notificationStatus, result.notificationMessage);
                    if (result.notificationStatus == StatusCode.success) {
                        fetchProfile();
                        navigate(-1);
                    }
                });
        } catch (error) {
            console.error('Error inserting user body measurements:', error);
        }
    }
    const insertUserGoals = async (entity: UserGoals) => {
        try {
            await UsersController.postUserGoals(entity)
                .then((result) => {
                    showAlert(result.notificationStatus, result.notificationMessage);
                    if (result.notificationStatus == StatusCode.success) {
                        fetchProfile();
                        navigate(-1);
                    }
                });
        } catch (error) {
            console.error('Error inserting user body measurements:', error);
        }
    }

    const isInRole = (role: Role) => {
        return userRole == role;
    }

    const fetchCategories = async () => {
        try {
            const result = await CategoriesController.get(tableSearchParam, searchParam);
            setCategories(result.categories);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };
    const fetchMeasurementUnits = async () => {
        try {
            const result = await MeasurementUnitsController.get(tableSearchParam, searchParam);
            setMeasurementUnits(result.measurementUnits);
        } catch (error) {
            console.error('Error fetching measurementUnits:', error);
        }
    };
    const fetchStores = async () => {
        try {
            const result = await StoresController.get(tableSearchParam, searchParam);
            setStores(result.stores);
        } catch (error) {
            console.error('Error fetching stores:', error);
        }
    };
    const fetchJournalNames = async () => {
        try {
            const result = await JournalNameController.get();
            setJournalNames(result.journalNames);
        } catch (error) {
            console.error('Error fetching journal names:', error);
        }
    }

    const context: UsersContextType = {
        isLoggedIn,
        userID,
        register,
        login,
        changePassword,
        logout,
        isInRole,

        profile,
        fetchProfile,
        updateProfile,
        insertBodyMeasurements,
        insertUserGoals,

        categories,
        measurementUnits,
        stores,
        journalNames,
        setJournalNames
    }

    return (
        <UsersContext.Provider value={context}>
            {children}
        </UsersContext.Provider>
    )
}

export const useUsersContext = () => useContext(UsersContext)
